'use client';

import type { CSSProperties } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { AD_INTEGRATIONS, STORE_INTEGRATIONS } from '@/lib/site-integrations';
import type { SiteIntegration } from '@/lib/site-integrations';

import './hero-integrations.css';

/**
 * Hero'nun entegrasyon şeridi — wask'tan kalma üç rozetin yerine (14 Eki 2026).
 *
 * Eski rozetler bir ortaklık İDDİASIYDI ("Google Ads Premium Partner", "Meta
 * Premium Partner", "OpenAI API Partner") ve yalnız iki reklam platformu
 * sayıyordu. Şerit artık iddia değil durum söylüyor: bugün hangi platform
 * bağlanıyor. Liste ve ölçütü `lib/site-integrations.ts`te; Kapsam bölümü
 * (`platforms.tsx`) aynı listeyi okuyor.
 *
 * İki grup, aralarında bir hat: reklam platformları ve mağazalar. Her grubun
 * başlığında yazılı durum var ("Reklam · 5 aktif") — durum yalnızca yeşil
 * noktayla anlatılmıyor (DESIGN.md §1). İşaretlerin adı `alt`ta; fareyle
 * üzerine gelince altında da yazıyor.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * İşaretler sırayla "takılıyor" (soluk + bulanık → net, 6px aşağıdan), grubun
 * noktası SON işaret oturduktan sonra yanıyor: önce bağlantılar, sonra
 * "aktif". Gruplar arka arkaya, okuma sırasıyla — önce reklam, sonra mağaza;
 * aradaki hat ikisinin arasında yukarıdan iniyor. Döngü yok.
 *
 * Uzun liste (8+, mağazalar: 11) iki şeyi değiştiriyor: kademe `stagger.tight`
 * — `base`te mağaza noktası ~1.3 sn'de yanıyordu, şimdi ~0.95 sn — ve blur
 * yok: 16 işaretin hepsi bulanıklıktan çözülseydi aynı anda ~15 öğelik blur
 * bütçesi (DESIGN.md §6) aşılırdı. Kısa listede blur kalıyor.
 *
 * Reduced motion: kökteki `MotionConfig reducedMotion="user"` y ve ölçeği
 * kapatıyor, yalnız opaklık/blur kalıyor — yer değiştiren bir şey olmuyor.
 * Hover kaydırması CSS'te ayrıca tercihe bağlı.
 */

/** 8+ öğe "uzun liste" (`stagger.tight`'ın tanımı). */
const isLong = (count: number) => count >= 8;

/** İşaretler arası aralık; noktanın gecikmesi de bundan türüyor. */
const markStagger = (count: number) => (isLong(count) ? stagger.tight : stagger.base);

const rootV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.1 } },
};

const groupV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

const captionV: Variants = {
  hidden: { opacity: 0, y: 4 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* `custom` = grubun işaret sayısı (listeye ve her işarete aynı değer). */
const listV: Variants = {
  hidden: {},
  show: (count: number) => ({ transition: { staggerChildren: markStagger(count) } }),
};

const markV: Variants = {
  hidden: (count: number) =>
    isLong(count) ? { opacity: 0, y: 6 } : { opacity: 0, y: 6, filter: 'blur(4px)' },
  show: (count: number) =>
    isLong(count)
      ? { opacity: 1, y: 0, transition: springFade(spring.default) }
      : { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
};

/* Nokta bir anahtar: kapanıyor, o yüzden snap yayı. Gecikme grubun işaret
   sayısından — son işaret yola çıktıktan hemen sonra. `springFade` burada
   kullanılmıyor: değer başına geçiş tanımlandığında üst düzey `delay`
   opaklığa geçmiyor, iki gecikme ayrı ayrı yazılmak zorunda. */
const dotV: Variants = {
  hidden: { opacity: 0, scale: 0.2 },
  show: (count: number) => {
    const delay = 0.06 + count * markStagger(count);
    return {
      opacity: 1,
      scale: 1,
      transition: {
        scale: { ...spring.snap, delay },
        opacity: { duration: dur.fast, ease: ease.out, delay },
      },
    };
  },
};

/* Gruplar arasındaki hat yukarıdan çekiliyor — bir çizim, o yüzden tween. */
const sepV: Variants = {
  hidden: { scaleY: 0, opacity: 0 },
  show: { scaleY: 1, opacity: 1, transition: { duration: dur.base, ease: ease.out } },
};

export function HeroIntegrations() {
  const t = useTranslations('hero.integrations');

  return (
    <motion.div
      className="hero-int"
      role="group"
      aria-label={t('label')}
      variants={rootV}
      initial="hidden"
      animate="show"
    >
      <IntegrationGroup
        variant="ads"
        kind={t('ads')}
        live={t('live', { count: AD_INTEGRATIONS.length })}
        listLabel={t('adsList')}
        items={AD_INTEGRATIONS}
      />
      <motion.span className="hero-int-sep" variants={sepV} aria-hidden="true" />
      <IntegrationGroup
        variant="stores"
        kind={t('stores')}
        live={t('live', { count: STORE_INTEGRATIONS.length })}
        listLabel={t('storesList')}
        items={STORE_INTEGRATIONS}
      />
    </motion.div>
  );
}

function IntegrationGroup({
  variant,
  kind,
  live,
  listLabel,
  items,
}: {
  variant: 'ads' | 'stores';
  kind: string;
  live: string;
  listLabel: string;
  items: readonly SiteIntegration[];
}) {
  return (
    <motion.div className={`hero-int-group hero-int-group--${variant}`} variants={groupV}>
      <motion.p className="hero-int-caption" variants={captionV}>
        <motion.span
          className="hero-int-dot"
          variants={dotV}
          custom={items.length}
          aria-hidden="true"
        />
        <span className="hero-int-kind">{kind}</span>
        <span className="hero-int-count">{live}</span>
      </motion.p>

      <motion.ul
        className="hero-int-list"
        aria-label={listLabel}
        variants={listV}
        custom={items.length}
      >
        {items.map((item) => (
          <motion.li
            key={item.key}
            className="hero-int-item"
            variants={markV}
            custom={items.length}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="hero-int-mark"
              src={item.markDark}
              alt={item.name}
              width={item.w}
              height={item.h}
              decoding="async"
              style={{ '--mark-scale': item.scale } as CSSProperties}
            />
            <span className="hero-int-tip" aria-hidden="true">
              {item.name}
            </span>
          </motion.li>
        ))}
      </motion.ul>
    </motion.div>
  );
}
